import {
  DEFAULT_ORIGIN,
  sortTeeth,
  spanProblems,
  type CaseItems,
  type ItemsReceived,
  type JobOrder,
  type Kit,
  type KitSpecification,
  type Origin,
  type Span,
  type SpanProblem,
} from '@/domain';
import { unitsFor } from '@/application/usecases/useIntake';
import type {
  CreateJobOrderCommand,
  UpdateJobOrderCommand,
} from '@/application/ports/JobOrderRepository';
import { strings } from '@/shared/strings';
import type { SpanRow } from './BridgesFields';

/**
 * Everything on a case that intake sets and an edit can change, as one value.
 *
 * `CaseFields` draws it, and intake and the edit screen each hold one, so the
 * two screens render the same fields and cannot drift. System test handover
 * 2.4, which asked for exactly this before the edit screen was built.
 */
export interface CaseFormValue {
  dentistName: string;
  patientName: string;
  /*
   * The doctor's and the patient's records, where one is chosen. The request
   * of 6 October 2026 (second), items 4 and 5. Null while the name beside it
   * is only a name: one nobody has typed before, which the server keeps as a
   * new record when the case is saved.
   *
   * Since the request of 6 October 2026 (third), item 3, a patient is one
   * record whichever doctors it has been to. The two are chosen apart, and
   * changing the doctor leaves the patient where it was.
   */
  doctorId: string | null;
  patientId: string | null;
  /**
   * The name typed is somebody's already, and the person at the desk has said
   * this is a different person of that name: the case makes a record of its
   * own. Only ever true beside a typed name and no `patientId`. Without it a
   * name means whoever already has it, so a second record of a name is never
   * made by accident.
   */
  newPatient: boolean;
  /**
   * The name typed is the name of more than one patient, and none of them is
   * chosen. Kept by the patient field, which is what looks the name up; the
   * form reads it to refuse the case until one is chosen, because the name
   * alone would quietly go to the oldest of them.
   */
  patientAmbiguous: boolean;
  /** Local, international or online, in place of the country. Section 1 of the request of 24 September 2026. */
  origin: Origin;
  input: 'digital' | 'impression';
  /** The case's headline item, and the default for a newly marked tooth. */
  product: string;
  /** Marked on the chart, in arch order. */
  teeth: string[];
  /** The item on each marked tooth, keyed by FDI number. */
  toothProducts: Record<string, string>;
  spanRows: SpanRow[];
  /** Text, so the box can be cleared; `caseUnits` reconciles it with the chart. */
  unitsText: string;
  shade: string;
  spec: KitSpecification;
  items: ItemsReceived;
  startDate: string;
  deliveryDate: string;
  /** The one instructions field. Section 5 of the request of 5 October 2026. */
  instructions: string;
  /**
   * The number on the paper job order handed to the doctor, as typed: optional.
   * The lab's request of 17 October 2026, item 7.
   */
  serialNumber: string;

  /*
   * A remake and its urgency, both decided while the case is being booked in.
   * Section 7 of the request of 16 September 2026. Nothing about the original is
   * copied; these two are the only fields it leaves on the new case.
   */
  /**
   * Null when this is not a remake, empty while the case is being searched for.
   * With `redoOfLegacy` it is the old number as it is being typed.
   */
  redoOf: string | null;
  /**
   * The original is from before this system, so there is nothing to find in
   * the archive: `redoOf` is its old job order number, typed by hand. The
   * request of 6 October 2026 (second), item 1.
   */
  redoOfLegacy: boolean;
  redoReason: string;
  urgent: boolean;
}

export type CaseChanges = UpdateJobOrderCommand['changes'];

export function emptyCase(startDate: string, deliveryDate: string): CaseFormValue {
  return {
    redoOf: null,
    redoOfLegacy: false,
    redoReason: '',
    urgent: false,
    dentistName: '',
    patientName: '',
    doctorId: null,
    patientId: null,
    newPatient: false,
    patientAmbiguous: false,
    origin: DEFAULT_ORIGIN,
    input: 'digital',
    product: 'Zircon Crown',
    teeth: [],
    toothProducts: {},
    spanRows: [],
    unitsText: '',
    shade: 'A2',
    spec: {
      translucency: 'medium',
      anatomy: 'medium',
      incisal: 'medium',
      occlusal: 'medium',
      ingot: '',
      block: '',
      stain: '',
    },
    items: {
      impression: false,
      model: false,
      bite: false,
      implant: false,
      abutment: false,
    },
    startDate,
    deliveryDate,
    instructions: '',
    serialNumber: '',
  };
}

/** A case as the edit screen opens it: what the server holds, in the form's shape. */
export function caseFromOrder(order: JobOrder, kit: Kit): CaseFormValue {
  const toothProducts: Record<string, string> = {};
  for (const item of kit.toothItems) toothProducts[item.tooth] = item.product;

  return {
    /*
     * Editing a case after intake never changes what it remakes or whether it
     * is urgent: what a case remakes is settled when it is booked in, and
     * urgency has its own endpoint. The edit form carries them so the value
     * shape is one shape, and the update command omits all three.
     */
    redoOf: order.redoOf,
    // Compared and not read: an API older than the field leaves it out.
    redoOfLegacy: order.redoOfLegacy === true,
    redoReason: order.redoReason ?? '',
    urgent: order.urgent,
    dentistName: kit.dentistName ?? '',
    patientName: kit.patientName ?? '',
    // The records the case is tied to, from the tray or the order, whichever
    // carries them. Absent without seeIdentity, as the two names are.
    doctorId: kit.doctorId ?? order.doctorId ?? null,
    patientId: kit.patientId ?? order.patientId ?? null,
    // A case as it stands is whose it is: nothing to decide until a name is typed.
    newPatient: false,
    patientAmbiguous: false,
    origin: order.origin,
    input: kit.input,
    product: kit.product,
    teeth: sortTeeth(kit.teeth),
    toothProducts,
    spanRows: order.spans.map((span) => ({
      key: span.id,
      name: span.name,
      kind: span.kind,
      from: span.from,
      to: span.to,
    })),
    unitsText: String(kit.units),
    shade: kit.shade,
    spec: { ...kit.specification },
    items: { ...kit.itemsReceived },
    startDate: kit.startDate,
    deliveryDate: kit.deliveryDate,
    // Defaulted although the type says string: the production client passes
    // responses through unvalidated. The incident of 23 September 2026.
    instructions: kit.instructions ?? '',
    // Absent from an API older than the field.
    serialNumber: order.serialNumber ?? '',
  };
}

/** The count the case carries: the larger of what is typed and what is marked. */
export function caseUnits(value: CaseFormValue): number {
  return unitsFor(value.teeth, Number(value.unitsText) || 0);
}

/** The chart as the contract carries it, each tooth with its item. */
export function caseTeeth(value: CaseFormValue): { tooth: string; product: string }[] {
  return value.teeth.map((tooth) => ({ tooth, product: value.toothProducts[tooth] ?? value.product }));
}

/**
 * What decides the case's route and its items: what a save of them asks about
 * and offers to undo. The lab's request of 18 October 2026.
 */
export function caseItems(value: CaseFormValue): CaseItems {
  return { input: value.input, product: value.product, teeth: caseTeeth(value) };
}

/** The bridges and bars as the contract carries them. */
export function caseSpans(
  value: CaseFormValue,
): { name: string; kind: SpanRow['kind']; from: string; to: string }[] {
  return value.spanRows.map(({ name, kind, from, to }) => ({ name: name.trim(), kind, from, to }));
}

/**
 * The doctor and the patient as a new case carries them: the record's id where
 * one is chosen, with the name beside it, and the name alone where it is new.
 * The request of 6 October 2026 (second), items 4 and 5. The server reads the
 * id when there is one, and keeps a name it has not seen as a new record.
 *
 * The two travel apart since the request of 6 October 2026 (third), item 3: a
 * patient is nobody's in particular, so its record goes whichever doctor is
 * beside it. `newPatient` goes only with a typed name and no record: it asks
 * for a second record of a name somebody already has, a different person, and
 * means nothing beside a patient chosen from the list.
 */
export function caseClients(
  value: CaseFormValue,
): Pick<
  CreateJobOrderCommand,
  'dentistName' | 'patientName' | 'doctorId' | 'patientId' | 'newPatient'
> {
  const patientName = value.patientName.trim();
  return {
    dentistName: value.dentistName.trim(),
    patientName,
    ...(value.doctorId !== null ? { doctorId: value.doctorId } : {}),
    ...(value.patientId !== null ? { patientId: value.patientId } : {}),
    ...(value.patientId === null && value.newPatient && patientName !== ''
      ? { newPatient: true }
      : {}),
  };
}

/**
 * What stops a case being saved for want of a choice of patient, or null. The
 * request of 6 October 2026 (third), item 3: a name that more than one patient
 * has, with none of them chosen, would go to the oldest of them without
 * anybody deciding it. Choosing one from the list, or saying it is a different
 * person, settles it.
 */
export function patientProblem(value: CaseFormValue): string | null {
  const undecided = value.patientAmbiguous && value.patientId === null && !value.newPatient;
  return undecided ? strings.intake.patientSeveral : null;
}

/** What a new case says of the case it remakes. */
export interface CaseRedo {
  redoOf: string;
  redoReason: string;
  /** Sent, and true, only for an original from before this system. */
  redoOfLegacy?: true;
}

/**
 * The remake as a new case carries it, or null for a case that is not one.
 *
 * An original from the archive is the case that was picked; ticked with none
 * picked is no remake, as it always was. An original from before this system
 * is the old number as typed, trimmed, and says so with `redoOfLegacy`: the
 * request of 6 October 2026 (second), item 1. That one is a remake as soon as
 * the choice is made, so an empty number is asked for rather than passed over.
 */
export function caseRedo(value: CaseFormValue): CaseRedo | null {
  if (value.redoOf === null) return null;

  const redoReason = value.redoReason.trim();
  if (value.redoOfLegacy) return { redoOf: value.redoOf.trim(), redoOfLegacy: true, redoReason };
  return value.redoOf === '' ? null : { redoOf: value.redoOf, redoReason };
}

/** The rows the chart can draw: both ends chosen. */
export function drawnSpans(rows: SpanRow[]): Span[] {
  return rows
    .filter((row) => row.from !== '' && row.to !== '')
    .map((row) => ({ id: row.key, name: row.name, kind: row.kind, from: row.from, to: row.to }));
}

function sameRecord(a: object, b: object): boolean {
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => left[key] === right[key]);
}

/**
 * What changed between the case as it was opened and as it stands, in the
 * shape the PATCH takes. Only these fields travel, so the server's activity
 * log names what was actually changed and nothing else.
 */
export function changesBetween(before: CaseFormValue, after: CaseFormValue): CaseChanges {
  const changes: CaseChanges = {};
  const text = (value: string) => value.trim();

  /*
   * The doctor and the patient, each by its record or by its name. The request
   * of 6 October 2026 (second), items 4 and 5. Another record chosen travels
   * as its id and nothing else; a name typed in place of the one the case had
   * travels as the name, and the server finds its record or makes one. A
   * record let go with the name left as it was is no change: the case keeps
   * what it has.
   *
   * The two change apart: the request of 6 October 2026 (third), item 3. A
   * doctor changed sends no word of the patient, who is the same patient
   * whichever doctor the case has. A name sent for a different person of that
   * name says so with `newPatient`, and is sent even when it reads as the name
   * the case already had, because then the name is not what changed.
   */
  if (after.doctorId !== null && after.doctorId !== before.doctorId) {
    changes.doctorId = after.doctorId;
  } else if (after.doctorId === null && text(after.dentistName) !== text(before.dentistName)) {
    changes.dentistName = text(after.dentistName);
  }
  if (after.patientId !== null && after.patientId !== before.patientId) {
    changes.patientId = after.patientId;
  } else if (after.patientId === null) {
    const name = text(after.patientName);
    const another = after.newPatient && name !== '';
    if (another || name !== text(before.patientName)) {
      changes.patientName = name;
      if (another) changes.newPatient = true;
    }
  }
  if (after.origin !== before.origin) changes.origin = after.origin;
  if (after.input !== before.input) changes.input = after.input;
  if (after.product !== before.product) changes.product = after.product;

  const teeth = caseTeeth(after);
  if (JSON.stringify(teeth) !== JSON.stringify(caseTeeth(before))) changes.teeth = teeth;

  const units = caseUnits(after);
  if (units !== caseUnits(before)) changes.units = units;

  if (text(after.shade) !== text(before.shade)) changes.shade = text(after.shade);
  if (!sameRecord(after.spec, before.spec)) changes.specification = after.spec;
  if (!sameRecord(after.items, before.items)) changes.itemsReceived = after.items;
  if (text(after.instructions) !== text(before.instructions)) {
    changes.instructions = text(after.instructions);
  }
  // Emptied, it comes off the case: null, which the server reads as taking it off.
  if (text(after.serialNumber) !== text(before.serialNumber)) {
    changes.serialNumber = text(after.serialNumber) === '' ? null : text(after.serialNumber);
  }

  const spans = caseSpans(after);
  if (JSON.stringify(spans) !== JSON.stringify(caseSpans(before))) changes.spans = spans;

  return changes;
}

/**
 * The message against each span row, or null where the row is sound.
 *
 * The domain's rules first, because they are the server's rules. Two checks
 * are the form's own: a row with no teeth chosen yet, which the server never
 * sees because the select offers nothing to send, and an empty name, which
 * the contract refuses with the same wording before any rule runs.
 */
export function spanRowErrors(rows: SpanRow[], teeth: string[]): (string | null)[] {
  const problems = spanProblems(rows, teeth);
  return rows.map((row, index) => {
    if (row.from === '' || row.to === '') return strings.intake.noTeeth;
    if (row.name.trim() === '') return strings.intake.spanNameRequired;
    const problem = problems[index] ?? null;
    return problem === null ? null : spanMessage(problem);
  });
}

/** A span problem in the form's words, which are the server's words. */
function spanMessage(problem: SpanProblem): string {
  switch (problem.rule) {
    case 'oneArch':
      return strings.intake.spanOneArch;
    case 'twoTeeth':
      return strings.intake.spanTwoTeeth;
    case 'unmarked':
      return strings.intake.spanUnmarked(problem.tooth);
    case 'sharedName':
      return strings.intake.spanSharedName;
    case 'overlap':
      return problem.kind === 'titaniumBar'
        ? strings.intake.spanOverlapBar
        : strings.intake.spanOverlapBridge;
  }
}

/** What a refused command says against each field, or nothing for a failure that names none. */
function fieldErrorsOf(failure: unknown): Record<string, string[] | undefined> {
  return typeof failure === 'object' && failure !== null && 'fieldErrors' in failure
    ? (failure as { fieldErrors: Record<string, string[]> }).fieldErrors
    : {};
}

/**
 * What was refused about the doctor, the patient and the two fields of a
 * remake, each to be said against its own field as well as beside the button.
 * The request of 6 October 2026 (second), items 1, 4 and 5. Null where the
 * field is sound.
 */
export interface CaseFieldErrors {
  doctor: string | null;
  patient: string | null;
  redoOf: string | null;
  redoReason: string | null;
}

/** One object for "nothing is wrong", so setting it twice changes nothing. */
export const NO_FIELD_ERRORS: CaseFieldErrors = {
  doctor: null,
  patient: null,
  redoOf: null,
  redoReason: null,
};

/**
 * The marks with the one on this field taken off: what touching the field does,
 * and nothing more. The same object when there was no mark on it, so a field
 * that moves without one draws nothing again; `NO_FIELD_ERRORS` once the last
 * mark is gone.
 */
export function withoutFieldError(errors: CaseFieldErrors, field: keyof CaseFieldErrors): CaseFieldErrors {
  if (errors[field] === null) return errors;
  const next: CaseFieldErrors = { ...errors, [field]: null };
  return Object.values(next).every((message) => message === null) ? NO_FIELD_ERRORS : next;
}

/**
 * Where a refusal lands. A doctor is named by a record or by a name, so a
 * refusal of either (`doctorId`, "Choose a doctor from the list.", or
 * `dentistName`, a name past its limit) is the doctor field's; the patient's
 * likewise.
 */
export function fieldErrorsFrom(failure: unknown): CaseFieldErrors {
  const fields = fieldErrorsOf(failure);
  const first = (...names: string[]) =>
    names.map((name) => fields[name]?.[0]).find((message) => message !== undefined) ?? null;

  const errors: CaseFieldErrors = {
    doctor: first('doctorId', 'dentistName'),
    patient: first('patientId', 'patientName'),
    redoOf: first('redoOf'),
    redoReason: first('redoReason'),
  };
  return Object.values(errors).every((message) => message === null) ? NO_FIELD_ERRORS : errors;
}

/** The server's span messages by row, from `errors["spans.<index>"]`. */
export function spanErrorsFrom(failure: unknown): Record<number, string> {
  const fields = fieldErrorsOf(failure);
  const byRow: Record<number, string> = {};
  for (const [key, messages] of Object.entries(fields)) {
    if (messages === undefined) continue;
    const match = /^spans\.(\d+)$/.exec(key);
    const message = messages[0];
    if (match !== null && message !== undefined) byRow[Number(match[1])] = message;
  }
  return byRow;
}

/** The form's own span messages, with the server's filling in where the form has none. */
export function mergeSpanErrors(
  client: readonly (string | null)[],
  server: Record<number, string>,
): (string | null)[] {
  return client.map((message, index) => message ?? server[index] ?? null);
}
